import React, { useEffect, useState } from "react";
import { Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import BlockIcon from "@material-ui/icons/Block";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";

const useStyles = makeStyles(
  (theme) => ({
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("sm")]: {
        padding: "1em",
      },
      fontFamily: "Roboto, sans-serif",
      opacity: 0.5,
    },
    title: {
      display: "flex",
      alignItems: "center",
    },
    icon: {
      width: "8em",
      height: "8em",
      marginBottom: "0.25em",
    },
    toolbar: {
      marginTop: "2em",
    },
  }),
  { name: "RaError" },
);

export const LOGOUT_DELAY_SECONDS = 15;

/**
 * Terminal screen for an authenticated user who has no portal to enter.
 *
 * Generalised from the carrier-only version: the customer branch needs the same
 * treatment, since a customer whose accounts are all inactive used to be handed
 * the new-yard signup flow instead. Both a button and the countdown reach
 * sign-out, so the user is never stranded on a page with no way out — and never
 * silently redirected either, which leaves them no idea why they were ejected.
 *
 * @param {string} title - headline, e.g. "Access Suspended"
 * @param {string} message - explanation; newlines render as line breaks
 * @param {Function} onSignOut - called by the button and when the countdown ends
 * @param {number} [delaySeconds] - seconds before automatic sign-out
 */
export const NoAccessScreen = ({
  title,
  message,
  onSignOut,
  delaySeconds = LOGOUT_DELAY_SECONDS,
}) => {
  const classes = useStyles();
  const [secondsLeft, setSecondsLeft] = useState(delaySeconds);

  useEffect(() => {
    if (secondsLeft === 0) {
      onSignOut();
      return;
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft, onSignOut]);

  return (
    <div className={classes.container}>
      <h1 className={classes.title} role="alert">
        <BlockIcon className={classes.icon} />
      </h1>
      <h1 className={classes.title}>{title}</h1>
      <Typography>
        {String(message ?? "")
          .split("\n")
          .map((line, index) => (
            <React.Fragment key={index}>
              {index > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
      </Typography>
      <Typography variant="body2">Signing out in {secondsLeft}s…</Typography>
      <div className={classes.toolbar}>
        <Button
          variant="contained"
          startIcon={<ExitToAppIcon />}
          onClick={onSignOut}
        >
          Sign Out Now
        </Button>
      </div>
    </div>
  );
};
